import { useEffect } from 'react';
import { RevivalLogo } from './components/RevivalLogo';
import { Box, Center, Container, SlideFade, Spinner } from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import useFetchData from './hooks/useFetchData';
import { AppointmentReminder } from '@revival/scheduler';
import { SelectAddressPanel } from './components/address/SelectAddressPanel';
import { AppointmentPanel } from './components/calendar/AppointmentPanel';
import { useSchedulerStore } from './stores/SchedulerStore';
import useToken from './hooks/useToken';
import { ConfirmationPage } from './pages/ConfirmationPage';
import { MissingPostalCodeModal } from './components/MissingPostalCodeModal';
import { createLogger } from '@revival/scheduler';

const log = createLogger('[App]');

function App() {
  const { reminderId } = useParams();
  const { token } = useToken();
  const navigate = useNavigate();

  const {
    selectedDate,
    selectedAddress,
    setReminder,
    getCalendarConfig,
    getAvailableAppointmentSlots,
    calendarConfig,
    setAvailableAppointmentSlots,
    displayConfirmation,
    isMissingPostalCode,
    resetMissingPostalCodeError,
  } = useSchedulerStore();

  const {
    data: reminder,
    loading,
    error,
  } = useFetchData<AppointmentReminder>(
    `${process.env.REACT_APP_REVIVAL_API_URL}/v1/reminder/${reminderId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  const facilityId = reminder?.patient?.organization?.charm_facility_id;

  useEffect(() => {
    const init = async () => {
      const calendarConfigResult = await getCalendarConfig(facilityId!, token);
      log.info('calendar config:', calendarConfigResult);
      log.info('selected date:', selectedDate?.toISOString() || '');
    };

    if (reminder && facilityId) {
      log.info('reminder:', reminder);
      setReminder(reminder);
      init();
    }

    if (error || (!loading && !reminder)) {
      navigate('/404');
    }
  }, [loading]); // eslint-disable-line

  useEffect(() => {
    setAvailableAppointmentSlots(null);
    const refreshSlots = async () => {
      const availableSlots = await getAvailableAppointmentSlots(
        selectedDate?.toISOString() || '',
        facilityId!,
        token,
      );
      log.info('available slots:', availableSlots);
    };

    if (selectedDate && facilityId) {
      refreshSlots();
    }
  }, [selectedDate, selectedAddress]); // eslint-disable-line

  if (displayConfirmation) return ConfirmationPage();

  return (
    <Container maxW={{ md: '100vw', xl: '75vw' }}>
      <Box
        w={{ base: '50vw', lg: '20vw' }}
        h={{ base: '40vw', lg: '15vw' }}
        m={'0 auto'}
        maxW={'360px'}
        maxH={'360px'}
      >
        <RevivalLogo />
      </Box>

      {reminder && !selectedAddress && (
        <SlideFade
          offsetY="20px"
          in={true}
          transition={{ exit: { delay: 0 }, enter: { duration: 0.5 } }}
        >
          <SelectAddressPanel />
        </SlideFade>
      )}

      {reminder && selectedAddress && calendarConfig && (
        <SlideFade
          offsetY="20px"
          in={true}
          transition={{ exit: { delay: 0 }, enter: { duration: 0.5 } }}
        >
          <AppointmentPanel />
        </SlideFade>
      )}

      {loading && !calendarConfig && (
        <Center>
          <Spinner size={'xl'} />
        </Center>
      )}

      <MissingPostalCodeModal
        isOpen={isMissingPostalCode}
        onClose={resetMissingPostalCodeError}
      />
    </Container>
  );
}

export default App;
